<template>
<v-expansion-panels
    multiple
    accordion
    flat
    tile
    v-model="componentState.expandedDocumentItems"
    class="document-items-expansion"
>
    <v-expansion-panel v-for="(item, index) in componentState.document.result.populatedData" :key="index" class="document-item-panel">
        <v-expansion-panel-header disable-icon-rotate class="document-item-header">
        <template v-slot:default="{ open }">
            <div class="full-width-header">
                <div class="header-left">
                    <v-icon class="chevron-icon" :class="{ 'rotated': open }">
                    mdi-chevron-right
                    </v-icon>
                    <v-checkbox
                        :input-value="isDocumentItemSelected(index)"
                        @change="toggleDocumentItem(index)"
                        @click.stop
                        :disabled="componentState.applyingDocuments"
                        hide-details
                        color="success"
                        dense
                        style="padding-left:5px"
                        class="document-checkbox"
                    ></v-checkbox>
                    <span class="document-part-name">{{ item.type }}</span>
                </div>
                <div class="header-right">
                    <v-progress-circular
                        v-if="componentState.document.regeneratingSection === item.type"
                        indeterminate
                        color="primary"
                        size="20"
                        width="2"
                        class="mr-2"
                    ></v-progress-circular>
                </div>
            </div>
        </template>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="document-item-content">
            <div class="content-holder" :class="[{'regenerating': componentState.document.regeneratingSection === item.type}]">
                <div v-if="item.values" class="document-values">
                    <!-- Single value display -->
                    <div v-if="item.values && item.values.length === 1" class="single-value">
                        <div v-if="item.values[0].tagRating">
                            <ul>
                                <li v-for="(skill,idx) in item.values[0].tagRating" :key="idx">
                                    {{ `${skill.text}${skill.rating ? `: ${skill.rating}` : ''}` }}
                                </li>
                            </ul>
                        </div>
                        <div v-else>
                            {{ typeof item.values[0] === 'object' ? (item.values[0].text || item.values[0].value || JSON.stringify(item.values[0])) : item.values[0] }}
                        </div>
                    </div>
                    <!-- Multiple values as bullet points -->
                    <ul v-else-if="item.values && item.values.length > 1" class="values-list">
                        <li v-for="(value, valueIndex) in item.values" :key="valueIndex" class="value-item">
                            {{ typeof value === 'object' ? (value.text || value.value || JSON.stringify(value)) : value }}
                            <!-- Child parts nested within this value -->
                            <ul v-if="typeof value === 'object' && value && value.child_parts && value.child_parts.length > 0" class="child-values-nested-list">
                                <li v-for="(childPart, childIndex) in value.child_parts" :key="childIndex" class="child-value-nested-item">
                                    {{ childPart }}
                                </li>
                            </ul>
                        </li>
                    </ul>
                    <!-- Fallback for no values -->
                    <div v-else class="no-values">
                        No values available
                    </div>
                </div>
                <!-- Justification section -->
                <div v-if="item.justification" class="justification-section">
                    <v-expansion-panels flat accordion class="justification-expansion">
                        <v-expansion-panel class="justification-panel">
                            <v-expansion-panel-header class="justification-header">
                                <div class="justification-header-content">
                                    Content Justification
                                    <v-icon small left color="#50B3CB" class="justification-icon">mdi-lightbulb-on-outline</v-icon>
                                </div>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content class="justification-content">
                                {{ item.justification }}
                            </v-expansion-panel-content>
                        </v-expansion-panel>
                    </v-expansion-panels>
                </div>
            </div>
        </v-expansion-panel-content>
    </v-expansion-panel>
</v-expansion-panels>
</template>

<script setup>
//#region Imports
import { ref, onBeforeMount, onUpdated, watch, reactive, getCurrentInstance, computed, nextTick  } from 'vue';
import axios from 'axios';
import { inject } from 'vue';
//#endregion

//#region Reactive
const props = defineProps({
    document: Object,
    selectedIndices: { type: Array, default: null }
});

const emit = defineEmits(['openDocument', 'accept', 'selectionChanged']);
const loginState = inject('loginState');
const store = inject('store');

const componentState = reactive({
    loading: false,
    notification: {
      text: "",
      type: "success"
    },
    document: null,
    expandedDocumentItems: [],
    selectedDocumentItems: new Set(),
});
//#endregion

//#region Trash

//#endregion

//#region Computed

//#endregion

//#region Methods

async function init(){
    if (componentState.loading) return;
    if(props.document){
        componentState.document = props.document;
    }
}

function isDocumentItemSelected(itemIndex) {
    return componentState.selectedDocumentItems.has(itemIndex);
}

function toggleDocumentItem(itemIndex) {
    if (componentState.selectedDocumentItems.has(itemIndex)) {
        componentState.selectedDocumentItems.delete(itemIndex);
    } else {
        componentState.selectedDocumentItems.add(itemIndex);
    }
    
    // Create new Set to trigger reactivity
    componentState.selectedDocumentItems = new Set(componentState.selectedDocumentItems);
    
    // Emit selection change to parent
    emitSelectionChange();
}

function selectAll() {
    if (!componentState.document?.result?.populatedData) return;
    
    componentState.selectedDocumentItems.clear();
    componentState.document.result.populatedData.forEach((item, index) => {
        componentState.selectedDocumentItems.add(index);
    });
    
    componentState.selectedDocumentItems = new Set(componentState.selectedDocumentItems);
    emitSelectionChange();
}

function clearAll() {
    componentState.selectedDocumentItems.clear();
    componentState.selectedDocumentItems = new Set();
    emitSelectionChange();
}

function getSelectedItems() {
    if (!componentState.document?.result?.populatedData) return [];
    
    return Array.from(componentState.selectedDocumentItems).map(index => ({
        index,
        item: componentState.document.result.populatedData[index]
    }));
}

function expandAll() {
    if (!componentState.document?.result?.populatedData) return;
    
    // Set all indices as expanded
    componentState.expandedDocumentItems = componentState.document.result.populatedData.map((_, index) => index);
}

function collapseAll() {
    // Clear all expanded items
    componentState.expandedDocumentItems = [];
}

function emitSelectionChange() {
    emit('selectionChanged', {
        selectedCount: componentState.selectedDocumentItems.size,
        totalCount: componentState.document?.result?.populatedData?.length || 0,
        selectedIndices: Array.from(componentState.selectedDocumentItems),
        selectedItems: getSelectedItems()
    });
}

function regeneratingItem(type){

}

function isDocumentResult(result) {
    return result && 
           result.success && 
           result.result && 
           typeof result.result === 'object' &&
           result.result.populatedData &&
           Array.isArray(result.result.populatedData) &&
           result.result.hierarchies &&
           Array.isArray(result.result.hierarchies);
}

// Expose methods for parent component
defineExpose({
    selectAll,
    clearAll,
    expandAll,
    collapseAll,    
    getSelectedItems,
    selectedCount: computed(() => componentState.selectedDocumentItems.size)
});

//#endregion

//#region Component
onUpdated(() => {
  init();
})

watch(() => props.document, () => init());

// Sync selection state from parent composable via prop
watch(() => props.selectedIndices, (newIndices) => {
    if (newIndices == null) return;
    const newSet = new Set(newIndices);
    const current = componentState.selectedDocumentItems;
    // Skip if already in sync
    if (current.size === newSet.size && [...current].every(i => newSet.has(i))) return;
    componentState.selectedDocumentItems = newSet;
}, { immediate: true });

onBeforeMount(() => {
    init();
})

import { onBeforeUnmount } from 'vue';

onBeforeUnmount(() => {

});
//#endregion
</script>

<style lang="scss" scoped>
/* ========================================
   BASE STYLES (Theme-neutral)
   ======================================== */

/* Document Items Expansion Panels */
.document-items-expansion {
    box-shadow: none;
}

::v-deep .document-item-panel {
    margin-bottom: 4px;
    border-radius: 6px;
    overflow: hidden;
}

::v-deep .document-item-header {
    min-height: 48px;
}

::v-deep .document-item-content .v-expansion-panel-content__wrap {
    padding: 0;
}

/* Content Holder */
.content-holder {
    width: 100%;
    height: 100%;
    padding: 12px 16px;
    position: relative;
}

/* Document Values */
.document-values {
    padding: 8px 0;
}

/* Justification Section */
.justification-section {
    padding-top: 10px;
}

::v-deep .justification-expansion {
    box-shadow: none;
}

::v-deep .justification-panel {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
}

::v-deep .justification-header {
    padding: 0 18px !important;
    border-left: 4px solid #50B3CB;
    border-radius: 6px 6px 0 0;
    font-size: 14px;
    font-weight: 600;
    min-height: 40px;
}

::v-deep .v-expansion-panel--active .justification-header {
    border-radius: 6px 6px 0 0;
}

.justification-header-content {
    display: flex;
    justify-content: start;
    align-items: center;
    padding-left: 10px;
}

.justification-icon {
    padding-left: 10px;
}

::v-deep .justification-content {
    border-left: 4px solid #50B3CB;
    border-radius: 0 0 6px 6px;
}

::v-deep .justification-content .v-expansion-panel-content__wrap {
    padding: 10px 18px;
    font-size: 14px;
    font-style: italic;
    font-family: 'Georgia', 'Times New Roman', Times, serif;
}

/* ========================================
   LIGHT MODE STYLES (using theme--light on elements)
   ======================================== */
.document-items-expansion.theme--light {
    ::v-deep .document-item-panel {
        background: #ffffff;
        border: 1px solid #e2e8f0;
    }

    ::v-deep .document-item-header {
        background: #ffffff;
        color: #1e293b;
        border-bottom: 1px solid #e2e8f0;

        &::before {
            background-color: #f7f9fb;
            opacity: 0;
        }

        &:hover {
            background: #f7f9fb;
            &::before {
                opacity: 0.1;
            }
        }

        &:focus, &:active {
            background: #f7f9fb;
            &::before {
                background-color: #f7f9fb;
                opacity: 0.1;
            }
        }
    }

    ::v-deep .v-expansion-panel--active .document-item-header {
        background: #f7f9fb;
        &::before {
            background-color: #f7f9fb;
            opacity: 0.1;
        }
    }

    ::v-deep .document-item-content {
        background: #f7f9fb;
    }

    ::v-deep .document-item-content .v-expansion-panel-content__wrap {
        background: #f7f9fb;
    }

    .content-holder {
        background: #ffffff;
        color: #1e293b;
    }

    .document-values {
        background-color: #f8fafc;
        color: #334155;
    }

    ::v-deep .justification-header {
        background: #f6f6fa;
        color: #1976d2;

        &::before {
            background-color: #f6f6fa;
            opacity: 0;
        }

        &:hover, &:focus, &:active {
            background: #eef0f5;
            &::before {
                background-color: #eef0f5;
                opacity: 0.1;
            }
        }
    }

    ::v-deep .v-expansion-panel--active .justification-header {
        &::before {
            background-color: #f6f6fa;
            opacity: 0.1;
        }
    }

    ::v-deep .justification-content {
        background: #f6f6fa;
    }

    ::v-deep .justification-content .v-expansion-panel-content__wrap {
        background: #f6f6fa;
        color: #333;
    }
}

/* ========================================
   DARK MODE STYLES (using theme--dark on elements)
   ======================================== */
.document-items-expansion.theme--dark {
    ::v-deep .document-item-panel {
        background: #272733 !important;
        border: 1px solid #3d3d50;
    }

    ::v-deep .document-item-header {
        background: #272733 !important;
        color: #e2e8f0 !important;
        border-bottom: 1px solid #3d3d50;

        &::before {
            background-color: #272733 !important;
            opacity: 0 !important;
        }

        &:hover {
            background: #1e1e2e !important;
            &::before {
                background-color: #1e1e2e !important;
                opacity: 0.1 !important;
            }
        }

        &:focus, &:active {
            background: #1e1e2e !important;
            &::before {
                background-color: #1e1e2e !important;
                opacity: 0.1 !important;
            }
        }
    }

    ::v-deep .v-expansion-panel--active .document-item-header {
        background: #1e1e2e !important;
        &::before {
            background-color: #1e1e2e !important;
            opacity: 0.1 !important;
        }
    }

    ::v-deep .document-item-content {
        background: #1e1e2e !important;
    }

    ::v-deep .document-item-content .v-expansion-panel-content__wrap {
        background: #1e1e2e !important;
        color: #e2e8f0;
    }

    .content-holder {
        background: #1e1e2e !important;
        color: #e2e8f0 !important;
    }

    .document-values {
        background-color: #272733 !important;
        color: #e2e8f0 !important;
    }

    .single-value {
        color: #e2e8f0 !important;
    }

    .values-list,
    .value-item {
        color: #e2e8f0 !important;
    }

    .child-values-nested-list,
    .child-value-nested-item {
        color: #cbd5e1 !important;
    }

    .document-part-name {
        color: #e2e8f0 !important;
    }

    .chevron-icon {
        color: #64748b !important;
    }

    ::v-deep .v-expansion-panel--active .chevron-icon {
        color: #e2e8f0 !important;
    }

    ::v-deep .justification-header {
        background: #2a2a3d !important;
        color: #64b5f6 !important;

        &::before {
            background-color: #2a2a3d !important;
            opacity: 0 !important;
        }

        &:hover, &:focus, &:active {
            background: #353548 !important;
            &::before {
                background-color: #353548 !important;
                opacity: 0.1 !important;
            }
        }
    }

    ::v-deep .v-expansion-panel--active .justification-header {
        &::before {
            background-color: #2a2a3d !important;
            opacity: 0.1 !important;
        }
    }

    ::v-deep .justification-content {
        background: #2a2a3d !important;
    }

    ::v-deep .justification-content .v-expansion-panel-content__wrap {
        background: #2a2a3d !important;
        color: #cbd5e1 !important;
    }
}

/* Mapping Error Splash Screen Styles */

.mapping-error-state {
    padding:20px;
}
.mapping-error-splash {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
}

.mapping-error-content {
  background: white;
  border-radius: 12px;
  padding: 48px;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  text-align: center;
}

.mapping-error-title {
  font-size: 26px;
  font-weight: 700;
  color: #333;
  margin: 0 0 16px 0;
}

.mapping-error-subtitle {
  font-size: 16px;
  color: #666;
  line-height: 1.6;
  margin: 0 0 32px 0;
}

.mapping-error-details {
  text-align: left;
  background: #f8f9fa;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 32px;
  
  h3 {
    font-size: 20px;
    font-weight: 600;
    color: #333;
    margin: 0 0 20px 0;
    text-align: center;
  }
}

.hierarchy-error-group {
  margin-bottom: 24px;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  h4 {
    font-size: 16px;
    font-weight: 600;
    color: #5E2872;
    margin: 0 0 12px 0;
    padding-bottom: 8px;
    border-bottom: 2px solid #e0e0e0;
  }
}

.error-steps-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.error-step-item {
  padding: 16px;
  background: white;
  border-radius: 6px;
  margin-bottom: 12px;
  border: 1px solid #e0e0e0;
  
  &:last-child {
    margin-bottom: 0;
  }
}

.step-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 12px;
  
  .step-name {
    font-size: 14px;
    font-weight: 600;
    color: #333;
  }
  
  .property-name {
    font-size: 13px;
    color: #666;
  }
}

.unmapped-value {
  display: flex;
  align-items: center;
  gap: 12px;
  
  .error-message {
    font-size: 13px;
    color: #d32f2f;
    font-weight: 500;
  }
}

.mapping-error-actions {
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
}

.generate-content-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 150px);
}

.header-section {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid #e0e0e0;
}

.page-title {
  font-size: 24px;
  font-weight: 500;
  color: #1976d2;
  margin: 0;
}

.subtitle {
  font-size: 14px;
  color: #666;
}

.loading-state, .empty-state, .execution-selection-state, .mapping-error-state {
  text-align: center;
  padding: 80px 20px;
}

.execution-selection-title {
  font-size: 24px;
  font-weight: 500;
  color: #333;
  margin-bottom: 12px;
}

.execution-selection-subtitle {
  font-size: 16px;
  color: #666;
  margin-bottom: 32px;
}

.execution-selection-actions {
  display: flex;
  justify-content: center;
  margin-bottom: 32px;
}

.previous-executions-list {
  max-width: 600px;
  margin: 0 auto;
  text-align: left;
}

.previous-executions-list h3 {
  font-size: 16px;
  color: #666;
  margin-bottom: 16px;
  text-align: center;
}

.execution-list-item {
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.execution-list-item:hover {
  background-color: #f5f5f5;
}

.loading-previous {
  margin-top: 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.results-container {
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.results-header {
  background: #5E2872;
  color: white;
  padding: 20px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.results-header h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 500;
}

.results-split-view {
  display: flex;
  flex: 1;
  height: 100%;
  background: white;
  min-height: 0; // Allow flex item to shrink below content size
}

.results-list-panel {
  width: 280px;
  border-right: 1px solid #e0e0e0;
  overflow-y: auto;
  background: #fafafa;
  flex-shrink: 0;
}

.result-item {
  padding: 16px;
  cursor: pointer;
  border-bottom: 1px solid #e0e0e0;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 12px;
}

.result-item:hover {
  background: #f0f0f0;
}

.result-item.active {
  background: #fce4f0;
  border-left: 4px solid #EA3785;
}

.result-item-icon {
  flex-shrink: 0;
}

.result-item-content {
  flex: 1;
  min-width: 0;
}

.result-item-title {
  font-weight: 500;
  font-size: 14px;
  color: #333;
}

.result-item-status {
  font-size: 12px;
  margin-top: 4px;
}

.result-detail-panel {
  flex: 1;
  overflow-y: auto;
  padding: 12px; // Reduced from 24px
  background: white;
  min-width: 0; // Allow flex item to shrink
}

.single-result-view-streamlined {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;

    max-height: 100%;
    overflow-y: hidden;  
}

.streamlined-result-output {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.detail-header {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid #f0f0f0;
}

.detail-header h3 {
  font-size: 22px;
  font-weight: 500;
  color: #333;
  margin: 0;
}

.detail-section {
  margin-bottom: 24px;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #e0e0e0;
}

.section-title {
  background: #f8f9fa;
  padding: 12px 16px;
  font-weight: 500;
  font-size: 14px;
  color: #555;
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid #e0e0e0;
}

.section-content {
  padding: 16px;
}

.input-table {
  width: 100%;
  border-collapse: collapse;
}

.input-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #f0f0f0;
  font-size: 14px;
}

.input-table td:first-child {
  font-weight: 500;
  color: #666;
  width: 30%;
}

.input-table tr:last-child td {
  border-bottom: none;
}

.result-output {
  background: #f8f9fa;
  border-radius: 4px;
  padding: 16px;
  font-family: 'Roboto Mono', monospace;
  font-size: 13px;
  line-height: 1.6;
  color: #333;
  white-space: pre-wrap;
  word-wrap: break-word;
  max-height: 500px;
  overflow-y: auto;
}

.processing-state {
  text-align: center;
  padding: 60px 20px;
}

.processing-state p {
  margin-top: 20px;
  font-size: 15px;
  color: #666;
}

.bulk-input-container {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}

.bulk-input-header {
  background: #50B3CB;
  color: white;
  padding: 20px 24px;
}

.bulk-input-header h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 500;
}

.bulk-input-subtitle {
  margin-top: 8px;
  font-size: 13px;
  opacity: 0.9;
}

.bulk-input-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
}

.bulk-input-table thead th {
  background: #f8f9fa;
  padding: 14px 12px;
  text-align: left;
  font-weight: 600;
  font-size: 13px;
  color: #555;
  border-bottom: 2px solid #e0e0e0;
  white-space: nowrap;
}

.bulk-input-table tbody td {
  padding: 12px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: middle;
}

.bulk-input-table tbody tr:hover {
  background: #fafafa;
}

.column-header-label {
  font-weight: 600;
  font-size: 13px;
  color: #333;
}

.column-header-meta {
  font-size: 11px;
  color: #999;
  font-weight: normal;
  display: block;
  margin-top: 2px;
}

.bulk-actions-footer {
  background: #f8f9fa;
  padding: 20px 24px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.execute-button {
  font-size: 15px !important;
  padding: 12px 32px !important;
  font-weight: 500 !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
}

.progress-text {
  font-size: 8px;
  font-weight: bold;
}

.progress-info {
  font-size: 11px;
  color: #999;
  margin-top: 2px;
}

.execution-info {
  font-size: 10px;
  color: #666;
  margin-top: 1px;
}

.chain-name {
  font-size: 10px;
  color: #999;
  font-weight: normal;
  margin-left: 4px;
}

.chain-name-header {
  font-size: 16px;
  color: #666;
  font-weight: normal;
}

.progress-bar-container {
  margin-top: 12px;
  margin-bottom: 16px;
}

.progress-text-detailed {
  font-size: 12px;
  color: #666;
  text-align: center;
}

.status-display {
  .status-item {
    margin-bottom: 8px;
    padding: 8px 0;
    border-bottom: 1px solid #f0f0f0;
    
    &:last-child {
      border-bottom: none;
      margin-bottom: 0;
    }
    
    strong {
      display: inline-block;
      width: 120px;
      color: #555;
    }
  }
}

.execution-summary {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #e0e0e0;
}

/* Document Creation Status Styles */
.document-creation-status-container {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.document-creation-header {
  background: #673ab7;
  color: white;
  padding: 20px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.document-creation-header h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 500;
}

.document-creation-content {
  flex: 1;
  overflow-y: auto;
  background: white;
  padding: 24px;
}

.document-creation-request {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 24px;
  background: white;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.request-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
}

.request-info h4 {
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: 500;
  color: #333;
}

.request-meta {
  display: flex;
  align-items: center;
  gap: 8px;
}

.timestamp {
  font-size: 13px;
  color: #666;
}

.progress-indicator {
  flex-shrink: 0;
}

.request-message {
  margin-bottom: 16px;
}

.processing-details {
  background: #f8f9fa;
  border-radius: 4px;
  padding: 12px 16px;
  margin-bottom: 16px;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  
  &:last-child {
    margin-bottom: 0;
  }
}

.detail-label {
  font-weight: 500;
  color: #555;
  min-width: 120px;
}

.detail-value {
  color: #333;
  flex: 1;
  text-align: right;
}

.document-rows {
  margin-top: 20px;
}

.document-rows h5 {
  margin: 0 0 12px 0;
  font-size: 16px;
  font-weight: 500;
  color: #333;
}

.rows-list {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}

.document-row {
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  gap: 12px;
}

.document-row:hover {
  background-color: #fafbfc;
}

.document-row.active {
  background: #fce4f0;
  border-left: 4px solid #EA3785;
}

.row-header {
  margin-bottom: 12px;
}

.row-primary-info {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 4px;
}

.row-number {
  font-weight: 600;
  color: #666;
  font-size: 12px;
  background: #f0f0f0;
  padding: 2px 6px;
  border-radius: 3px;
  min-width: 40px;
  text-align: center;
}

.doc-name {
  font-weight: 600;
  color: #333;
  font-size: 16px;
  flex: 1;
}

.row-secondary-info {
  display: flex;
  gap: 16px;
  margin-left: 52px; // Align with doc name
  flex-wrap: wrap;
}

.doc-id, .doc-reference {
  font-size: 12px;
  color: #666;
  background: #f8f9fa;
  padding: 2px 8px;
  border-radius: 3px;
  border: 1px solid #e0e0e0;
}

.doc-reference {
  background: #e3f2fd;
  border-color: #bbdefb;
  color: #1976d2;
}

.row-details {
  margin-top: 12px;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 8px 16px;
}

.detail-grid .detail-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 8px;
}

.detail-grid .detail-label {
  font-size: 11px;
  text-transform: uppercase;
  color: #999;
  font-weight: 600;
  letter-spacing: 0.5px;
  min-width: auto;
}

.detail-grid .detail-value {
  font-size: 13px;
  color: #333;
  font-weight: 500;
  text-align: left;
}

.user-id {
  font-family: monospace;
  font-size: 12px;
  color: #666;
}

.row-message, .row-error {
  margin-top: 8px;
}

.results-summary {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
}

.results-summary h5 {
  margin: 0 0 12px 0;
  font-size: 16px;
  font-weight: 500;
  color: #333;
}

.summary-stats {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
}

.stat-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.stat-label {
  font-size: 12px;
  color: #666;
  font-weight: 500;
}

.stat-value {
  font-size: 18px;
  font-weight: 600;
}

/* Document result styles */
.document-result {
  border: 1px solid #e0e0e0;
  overflow: hidden;
  max-height: calc(100vh - 120px);
}

.document-actions-header {
  padding: 12px; // Reduced from 16px
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.back-to-selection {
  width: 100%;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
  margin-bottom: 4px;
}

.document-selection-info {
  font-size: 14px;
  font-weight: 500;
  color: #555;
}

.document-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  padding-left: 5px;
}

.document-actions .v-btn {
  text-transform: none;
  font-weight: 500;
  letter-spacing: 0;
  font-size: 12px;
}

.document-items-list {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  max-height: calc(100vh - 205px);
}

.text-result-streamlined {
  background: #f8f9fa;
  border-radius: 4px;
  padding: 16px;
  font-family: 'Roboto Mono', monospace;
  font-size: 13px;
  line-height: 1.6;
  color: #333;
  white-space: pre-wrap;
  word-wrap: break-word;
  max-height: 500px;
  overflow-y: auto;
}

/* Update styles for nested child parts within values */
.child-values-nested-list {
  margin: 4px 0 0 16px;
  padding-left: 16px;
  list-style-type: circle;
}

.child-value-nested-item {
  font-size: 12px;
  line-height: 1.4;
  margin-bottom: 4px;
  color: #666;
  
  strong {
    font-weight: 600;
    color: #333;
  }
}

.nested-child-values-list {
  margin: 2px 0 0 12px;
  padding-left: 12px;
  list-style-type: square;
}

.nested-child-value-item {
  font-size: 11px;
  line-height: 1.3;
  margin-bottom: 2px;
  color: #555;
}

.results-header-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.bulk-document-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-right: 16px;
  border-right: 1px solid rgba(255, 255, 255, 0.3);
  flex-wrap: wrap;
}

.bulk-document-actions .v-btn {
  font-size: 11px;
  padding: 0 8px;
  min-width: auto;
}

.document-item-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid #e8e8e8;
  cursor: pointer;
  user-select: none;
  transition: all 0.15s ease-out; // Faster, smoother transition
  border-left: 3px solid transparent;
  position: relative; // Prevent z-index conflicts
  
  &:hover:not(.selected) {
    border-left-color: #50B3CB;
    box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  }
  
  &.expanded-header {
    border-left-color: #28a745;
    font-weight: 500;
    
    // Override hover when expanded to prevent conflicts
    &:hover {
      border-left-color: #28a745; // Keep same border
    }
  }
}

// Enhanced document item styling with conflict prevention
.document-item {
  border-bottom: 1px solid #f0f0f0;
  transition: box-shadow 0.15s ease-out; // Only animate shadow
  border-radius: 8px;
  margin-bottom: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  position: relative; // For overlay positioning
  
  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }
  
  &:hover:not(.selected) {
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  }
  
  &.selected {
    border: 2px solid #4caf50;
    box-shadow: 0 2px 8px rgba(76,175,80,0.15) !important;
    
    .document-item-header {
      
      // Prevent hover effects when selected
      &:hover {
        border-left-color: #4caf50 !important;
        box-shadow: none; // Remove hover shadow when selected
      }
    }
  }
  
}

.content-processing-overlay {
  position: absolute;
  top: -2px;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.80) 0%, rgba(248, 250, 252, 0.80) 100%);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000; 
  border-radius: 4px;
  border: 1px solid rgba(59, 130, 246, 0.2);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  pointer-events: auto;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, #3b82f6, #8b5cf6, #06b6d4, #3b82f6);
    background-size: 200% 100%;
    animation: shimmer 2s linear infinite;
  }
  
  .processing-content {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    min-width: 160px;
    max-width: 90%;
    justify-content: center;
    transition: all 0.3s ease;
    flex-shrink: 1;
    
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    }
    
    .processing-text {
      font-size: 12px;
      font-weight: 600;
      color: #374151;
      white-space: nowrap;
      letter-spacing: 0.025em;
      position: relative;
      text-overflow: ellipsis;
      overflow: hidden;
    }
    
    .v-progress-circular {
      filter: drop-shadow(0 2px 4px rgba(59, 130, 246, 0.2));
      flex-shrink: 0;
    }
  }
}

.document-part-name {
  font-weight: 600;
  font-size: 14px;
  color: #495057;
  flex: 1;
  transition: color 0.15s ease-out; // Smooth color transition
  padding-top: 2px;
  
  // Prevent text color conflicts
  .document-item.selected & {
    color: #155724 !important; // Override for selected state
  }
  
  .document-item:not(.selected) .document-item-header:hover & {
    color: #343a40;
  }
  
  .document-item:not(.selected) .document-item-header.expanded-header & {
    color: #155724;
  }
}

.expand-icon {
  transition: transform 0.2s ease-out, color 0.15s ease-out; // Separate transitions
  color: #6c757d;
  opacity: 0.8; // Slightly more visible
  
  &.expanded {
    transform: rotate(180deg);
    color: #28a745;
    opacity: 1;
  }
  
  // Prevent icon color conflicts
  .document-item.selected & {
    color: #155724 !important;
    
    &.expanded {
      color: #155724 !important;
    }
  }
  
  .document-item:not(.selected) .document-item-header:hover & {
    opacity: 1;
    color: #50B3CB;
    
    &.expanded {
      color: #28a745;
    }
  }
}

// Ensure checkbox doesn't cause conflicts
.document-checkbox {
  flex-shrink: 0; // Prevent size changes
  pointer-events: auto; // Ensure checkbox clicks work properly
}

// Remove any conflicting transitions from child elements
.document-item-content {
  padding: 20px 24px;
  border-bottom: 1px solid #e1e4e8;
  // Remove transition to prevent conflicts
  
  &:last-child {
    border-bottom: none;
  }
}

.regenerate-actions {
    display: flex;
    gap: 8px;
    align-items: center;
}

.v-list-item-subtitle {
    font-size: 11px;
    color: #999;
    margin-top: 2px;
}

.full-width-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 12px 16px;

  .active {
    background-color:#F7F9FB;
  }
}

.header-left {
  display: flex;
  align-items: center;
  flex: 1;
}

.header-right {
  display: flex;
  align-items: center;
}

.chevron-icon {
  font-size: 18px !important;
  color: #AAAAAA;
  transition: transform 0.2s ease, color 0.2s ease;
}

.chevron-icon.rotated {
  transform: rotate(90deg);
}

::v-deep .v-expansion-panel--active .chevron-icon {
  color: #000000;
}

.header-title {
  font-weight: 400;
  color: #000000;
  font-size: 14px;
  transition: font-weight 0.2s ease;
}

::v-deep .v-expansion-panel--active .header-title {
  font-weight: 700;
  font-style: bold;
}

.clear-selection-btn {
  box-sizing: border-box;
  display: flex !important;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 2px 8px !important;
  gap: 4px;
  margin: 0 auto;
  min-width: 89px !important;
  height: 24px !important;
  background: #EEEEEE !important;
  border: 1px solid #EEEEEE !important;
  border-radius: 8px !important;
  flex: none;
  order: 1;
  flex-grow: 0;
  font-size: 10px !important;
  font-weight: 500;
  text-transform: none !important;
  letter-spacing: normal !important;
  color: #666666 !important;
}

.clear-selection-btn:before {
  display: none !important;
  background-color: #EEEEEE;
}

.clear-icon {
  margin-right: 2px !important;
  font-size: 18px !important;
  color: #666666 !important;
  padding-left: 2px !important;
}

.count-badge {
  background-color: #FFF !important;
  color: #000000 !important;
  font-size: 9px !important;
  height: 16px !important;
  min-width: 16px !important;
  padding: 0 4px !important;
  margin: 0 4px !important;
  border-radius: 8px !important;
  left: 2px;
  bottom: 1px;
}

.count-badge ::v-deep .v-chip__content {
  padding: 0 !important;
  line-height: 18px !important;
  font-weight: 600 !important;
  padding-left: 1px !important;
  padding-right: 2px !important;
}

.document-values{
    
    background-color: white;
    border-radius: 16px;
    opacity: 1;
    padding-top: 16px;
    padding-right: 8px;
    padding-bottom: 16px;
    padding-left: 8px;
    position: relative; // Required for absolute positioned overlay
}

/* Expansion Panels */
::v-deep .v-expansion-panels {
  box-shadow: none;
}

::v-deep .v-expansion-panel {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}

::v-deep .v-expansion-panel-header {
  padding: 0 !important;
  background: #ffffff;
  font-weight: 600;
  color: #1e293b;
  font-size: 14px;
  border-bottom: 1px solid #e2e8f0;
  transition: all 0.2s ease;
  min-height: 40px;


}

::v-deep .v-expansion-panel--active .v-expansion-panel-header {
  background: #F7F9FB;
  :focus {
    background-color: #F7F9FB;
  }
}

::v-deep .v-expansion-panel-header:hover {
  background: #F7F9FB;
}

::v-deep .v-expansion-panel-header__icon {
  display: none !important;
}

::v-deep .v-expansion-panel-content__wrap {
  padding: 0px;
  overflow-y: auto;
  margin-bottom: 1px;
  background: #F7F9FB;
}

::v-deep .v-expansion-panel--active .header-title {
  font-weight: 700;
  font-style: bold;
}

/* Remove the focus background from header when checkbox is focused */
.v-expansion-panel-header:focus-within {
  background-color: #F7F9FB;
}

/* Or be more specific to only affect when checkbox has focus */
.v-expansion-panel-header:has(input[type="checkbox"]:focus) {
  background-color: #F7F9FB;
}

/* You might also need to prevent the ripple effect */
.v-expansion-panel-header::before {
  background-color: #F7F9FB;
}

.content-holder{
    width:100%;
    height:100%;
    padding: 12px 16px;
    position:relative;

    &.regenerating {
        pointer-events: none;
        &::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 2px;
            background: linear-gradient(90deg, #3b82f6, #8b5cf6, #06b6d4, #3b82f6);
            background-size: 200% 100%;
            animation: shimmer 2s linear infinite;
            z-index: 10;
        }
        &::after {
        content: 'Regenerating Content...';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(135deg, rgba(255, 255, 255, 0.80) 0%, rgba(248, 250, 252, 0.80) 100%);
        backdrop-filter: blur(2px);
        z-index: 10;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        font-weight: 600;
        color: #374151;
        letter-spacing: 0.025em;
        }
    }
}
</style>
